import axios from "axios";

// --- Define Survey Schema Types ---
interface SurveyChoice {
  text: string;
  value: string;
}

interface SurveyElement {
  name: string;
  type: string; // e.g., 'radiogroup', 'comment', 'text'
  title: string;
  choices?: SurveyChoice[];
  isRequired?: boolean;
  colCount?: number;
  otherText?: string;
  showOtherItem?: boolean;
  // Add other potential element properties
}

interface SurveyPage {
  name: string;
  elements: SurveyElement[];
}

export interface SurveySchema {
  pages: SurveyPage[];
  title: string;
  description: string;
  // Add other potential schema properties
}
// --- End Survey Schema Types ---

// Interface for the data needed to create/update a form
export interface FeedbackFormInput {
  schema: SurveySchema | Record<string, never>; // Allow empty object or full schema
  projectId: string;
  displayName: string;
  serviceMetadata: {
    source: "WEB" | "MOBILE" | string;
  };
  surveyId?: string;
  surveyVersion?: string;
}

// Update the response structure to match the actual API response
export interface FeedbackFormResponse {
  // Use surveyId instead of id
  surveyId: string;
  surveyVersion: string;
  status: string;
  // Use displayName instead of name
  displayName: string;
  // Keep schema definition
  schema?: SurveySchema; // Assuming SurveySchema defined above matches
  createdUserId: string;
  serviceMetadata?: {
    // Optional based on sample
    source?: string;
    linkedService?: string;
  };
  projectId: string;
  createdAt: string;
  updatedAt: string;
  // Remove fields not present in sample (like description directly on root)
  // description?: string;
  // id?: number | string; // Removed
  // name?: string; // Removed
}

class MfaFeedbackService {
  private static instance: MfaFeedbackService;
  private baseUrl = `${process.env.REACT_APP_API_BASE_URL}/flight-recorder`;

  private constructor() {}

  public static getInstance(): MfaFeedbackService {
    if (!MfaFeedbackService.instance) {
      MfaFeedbackService.instance = new MfaFeedbackService();
    }
    return MfaFeedbackService.instance;
  }

  /**
   * Fetches feedback forms associated with a specific project ID.
   * @param token - The user authentication token.
   * @param projectId - The ID of the project.
   * @returns A promise that resolves to an array of feedback form responses.
   */
  public async getFeedbackFormsByProject(
    token: string,
    projectId: string,
  ): Promise<FeedbackFormResponse[]> {
    if (!projectId) {
      console.error("Project ID is required to fetch feedback forms.");
      throw new Error("Project ID is required.");
    }

    try {
      const response = await axios.get<FeedbackFormResponse[]>(
        `${this.baseUrl}/feedback-form/project/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );
      console.log("getFeedbackFormsByProject response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch feedback forms by project:", error);
      //   if (axios.isAxiosError(error)) {
      //     const axiosError = error; // No need for AxiosError<{ message?: string }> assertion here
      //     throw new Error(
      //       `API Error (${axiosError.response?.status}): ${axiosError.message}`, // Use axiosError.message
      //     );
      //   }
      if (error instanceof Error) {
        throw new Error(`Failed to fetch feedback forms: ${error.message}`);
      }
      throw new Error(
        "An unknown error occurred while fetching feedback forms.",
      );
    }
  }

  /**
   * Creates a new feedback form.
   * @param token - The user authentication token.
   * @param userid - The user ID for the header.
   * @param formData - The data for the new feedback form.
   * @returns A promise that resolves to the created feedback form response.
   */
  public async createFeedbackForm(
    token: string,
    userid: string,
    formData: FeedbackFormInput,
  ): Promise<FeedbackFormResponse> {
    try {
      const response = await axios.post<FeedbackFormResponse>(
        `${this.baseUrl}/feedback-form`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            userid: userid, // Add userid header
            "Content-Type": "application/json",
            accept: "application/json",
          },
        },
      );
      console.log("createFeedbackForm response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to create feedback form:", error);
      if (error instanceof Error) {
        // Enhance error message if possible (e.g., from Axios response)
        const message =
          (error as { response?: { data?: { message?: string } } }).response
            ?.data?.message || error.message;
        throw new Error(`Failed to create feedback form: ${message}`);
      }
      throw new Error(
        "An unknown error occurred while creating feedback form.",
      );
    }
  }

  /**
   * Updates an existing feedback form.
   * @param token - The user authentication token.
   * @param userid - The user ID for the header.
   * @param surveyId - The ID of the survey form to update.
   * @param formData - The updated data for the feedback form.
   * @returns A promise that resolves to the updated feedback form response.
   */
  public async updateFeedbackForm(
    token: string,
    userid: string,
    // surveyId: string, // ID of the form to update
    formData: Partial<FeedbackFormInput>, // Use Partial as not all fields might be updatable
  ): Promise<FeedbackFormResponse> {
    try {
      const response = await axios.put<FeedbackFormResponse>(
        `${this.baseUrl}/feedback-form`, // Assumed PUT endpoint
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            userid: userid,
            "Content-Type": "application/json",
            accept: "application/json",
          },
        },
      );
      console.log("updateFeedbackForm response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to update feedback form:", error);
      if (error instanceof Error) {
        const message =
          (error as { response?: { data?: { message?: string } } }).response
            ?.data?.message || error.message;
        throw new Error(`Failed to update feedback form: ${message}`);
      }
      throw new Error(
        "An unknown error occurred while updating feedback form.",
      );
    }
  }
}

export default MfaFeedbackService;
